/**
 * DateFilter Component
 * TASK-207: Portfolio Level View - Enhanced Filters
 *
 * Toggle between latest data and historical snapshot
 * Includes date picker for selecting point-in-time view
 *
 * TASK-877: `Historical Snapshot` rendered as a fully live button — same size, same
 * hover, same `cursor-pointer` as `Latest` — while `handleModeChange` set an error
 * and returned early whenever `historicalSupported` was false, which is its default
 * and its only value in this codebase (`MyBookView.tsx:190`). `dateMode` therefore
 * never became `'historical'`, so every explanation of what had happened —
 * the picker, the `dateError` line, the yellow "coming soon" note — sat behind
 * `{dateMode === 'historical' && …}` and was unreachable by construction. The
 * advisor clicked a live-looking control, nothing moved, and nothing said why.
 *
 * The acceptance criterion allows "opens a date picker and re-queries, **or is
 * removed**", and removed is the right half here. Review caught the first cut disabling
 * the button and adding a permanent yellow "not available" banner: `ClientLevelView` is
 * the only surface that renders this filter (`MyBookView` and `PortfolioDashboardPresenter`
 * both pass `showDateFilter={false}`), so that banner would sit on the client portfolio
 * page forever — the same page this ticket separately reports as having *zero data above
 * the fold at 1280x800*. Explaining a control nobody can use, in permanent vertical space,
 * on the page that has no room, trades one defect for a worse one.
 *
 * So when the backend cannot answer for a past date there is no toggle at all: one line
 * stating which data is shown. The picker path below is untouched and the toggle returns
 * whole the moment `historicalSupported` is true — this removes the lie, not the feature.
 */

import React, { useState } from 'react';
import { Calendar, AlertCircle } from 'lucide-react';
import { format, parse, isValid, isFuture, isAfter, subYears } from 'date-fns';

interface DateFilterProps {
  dateMode: 'latest' | 'historical';
  historicalDate?: string;
  onChange: (dateMode: 'latest' | 'historical', historicalDate?: string) => void;
  disabled?: boolean;
  historicalSupported?: boolean; // Backend support flag
  minDate?: Date; // Earliest available historical date
}

const DateFilter: React.FC<DateFilterProps> = ({
  dateMode,
  historicalDate,
  onChange,
  disabled = false,
  historicalSupported = false, // Default: not yet supported
  minDate,
}) => {
  const [dateError, setDateError] = useState<string>('');

  // Calculate min/max dates
  const today = new Date();
  const earliestDate = minDate || subYears(today, 10); // Default: 10 years back
  const earliestDateStr = format(earliestDate, 'yyyy-MM-dd');
  const todayStr = format(today, 'yyyy-MM-dd');

  const historicalUnavailable = !historicalSupported;

  const handleModeChange = (newMode: 'latest' | 'historical') => {
    if (disabled) return;
    // The button is disabled in this case, so this is belt-and-braces rather than the
    // control flow. It used to set a `dateError` that could never render — the only
    // element that displays it lives inside the `historical` branch this return
    // prevents us from entering.
    if (newMode === 'historical' && historicalUnavailable) return;

    setDateError('');

    if (newMode === 'latest') {
      onChange('latest', undefined);
    } else {
      // When switching to historical, default to today if no date set
      onChange('historical', historicalDate || todayStr);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;

    // Validate date
    const parsedDate = parse(newDate, 'yyyy-MM-dd', new Date());

    if (!isValid(parsedDate)) {
      setDateError('Invalid date');
      return;
    }

    if (isFuture(parsedDate)) {
      setDateError('Cannot select future dates');
      return;
    }

    if (isAfter(earliestDate, parsedDate)) {
      setDateError(`Date cannot be earlier than ${format(earliestDate, 'dd.MM.yyyy')}`);
      return;
    }

    setDateError('');
    onChange('historical', newDate);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Data Date
      </label>

      {/* Mode Toggle — only when there is a choice to make. */}
      {!historicalUnavailable && (
      <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
        <button
          type="button"
          onClick={() => handleModeChange('latest')}
          disabled={disabled}
          className={`
            px-4 py-2 rounded-md text-sm font-medium
            transition-colors duration-200
            ${
              dateMode === 'latest'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          Latest
        </button>
        {/* Reached only when historical IS supported — the toggle is not rendered
            otherwise — so this button is live whenever it exists. */}
        <button
          type="button"
          onClick={() => handleModeChange('historical')}
          disabled={disabled}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
            transition-colors duration-200
            ${
              dateMode === 'historical'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <Calendar className="w-4 h-4" />
          Historical Snapshot
        </button>
      </div>
      )}

      {/* Date Picker (shown when historical mode) */}
      {dateMode === 'historical' && (
        <div className="mt-2">
          <input
            type="date"
            value={historicalDate || ''}
            onChange={handleDateChange}
            min={earliestDateStr}
            max={todayStr}
            disabled={disabled || !historicalSupported}
            className={`
              w-full px-3 py-2 border rounded-md shadow-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              ${
                dateError
                  ? 'border-red-300'
                  : 'border-gray-300'
              }
              ${disabled || !historicalSupported ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
            `}
          />
          {dateError && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {dateError}
            </p>
          )}
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-gray-500">
        {dateMode === 'latest'
          ? 'Showing current portfolio data'
          : historicalDate
          ? `Viewing portfolio as of ${format(parse(historicalDate, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')}`
          : 'Select a date to view historical snapshot'}
      </p>
    </div>
  );
};

export default DateFilter;
